import axios from "axios";
import { ErrorMessage, Field, useField, useFormikContext } from "formik";
import React, { useCallback, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import classNames from "classnames";
import { GoogleMaps } from "../GoogleMaps";
import { getAppEnv } from "core/utils/runtimeConfig";

const MapField: ({
    label,
    required,
    backgroundColor,
    name,
    type,
    placeHolder,
    customHeight,
    isMobile,
    labelClassNames,
    labelStyle,
    mapField,
    mapName,
    customFontSize,
    customStyle,
    disable,
    dominCheck,
    countryCodeName,
    latitudeName,
    longitudeName,
    geofenceRadiusName,
}: {
    label?: string;
    required?: boolean;
    backgroundColor?: string;
    name: string;
    mapName?: string;
    type?: string;
    placeHolder?: string;
    customHeight?: string;
    labelClassNames?: string;
    labelStyle?: React.CSSProperties;
    mapField?: boolean;
    customFontSize?: string;
    customStyle?: any;
    disable?: boolean;
    isMobile?: boolean;
    dominCheck?: boolean;
    countryCodeName?: string;
    wantButton?: string;
    initialValue?: string;
    latitudeName?: string;
    longitudeName?: string;
    geofenceRadiusName?: string;
}) => React.JSX.Element = ({
    label,
    required,
    backgroundColor,
    name,
    type,
    placeHolder,
    mapField,
    mapName,
    labelClassNames,
    labelStyle,
    customHeight,
    customFontSize,
    customStyle,
    disable,
    isMobile,
    dominCheck,
    countryCodeName,
    wantButton,
    latitudeName,
    longitudeName,
    geofenceRadiusName,
}) => {
        const mapFieldName = mapName || "GOOGLE_MAP_POSITION";
        const [, , helpers] = useField(mapFieldName);
        const { setFieldValue } = useFormikContext<any>();
        const { setValue } = helpers;
        const lastGeocodedAddressRef = React.useRef<string>("");

        const setLocationFieldValues = useCallback((location, form?: any) => {
            const formSetFieldValue = form?.setFieldValue || setFieldValue;

            if (formSetFieldValue) {
                formSetFieldValue(mapFieldName, location || "");
                if (latitudeName) {
                    formSetFieldValue(latitudeName, location?.lat || 0);
                }
                if (longitudeName) {
                    formSetFieldValue(longitudeName, location?.lng || 0);
                }
            } else {
                setValue(location || "");
            }
        }, [latitudeName, longitudeName, mapFieldName, setFieldValue, setValue]);

        const updateLocationFromAddress = useCallback(async (enteredAddress, form) => {
            const normalizedAddress = String(enteredAddress || "").trim();
            if (!normalizedAddress || normalizedAddress.length < 3 || normalizedAddress === lastGeocodedAddressRef.current) {
                return;
            }

            try {
                const response = await axios.get(
                    `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                        normalizedAddress
                    )}&key=${getAppEnv("REACT_APP_GOOGLE_API_KEY") || ""}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (response?.data?.results?.length > 0) {
                    const location = response?.data?.results?.[0]?.geometry?.location;
                    const formattedAddress =
                        response?.data?.results?.[0]?.formatted_address;
                    let place_id = response?.data?.results?.[0]?.place_id;
                    let city = "";
                    let postalCode = "";
                    let country = "";
                    let state = "";
                    response?.data?.results?.[0]?.address_components.forEach(
                        (component) => {
                            if (component.types.includes("postal_code")) {
                                postalCode = component.long_name;
                            }
                            if (component.types.includes("country")) {
                                country = component.long_name;
                            }
                            if (component.types.includes("administrative_area_level_3")) {
                                city = component.long_name;
                            }
                            if (component.types.includes("administrative_area_level_1")) {
                                state = component.long_name;
                            }
                        }
                    );
                    const value = {
                        lat: location.lat,
                        lng: location.lng,
                        address: formattedAddress,
                        city,
                        state,
                        country,
                        postalCode,
                        place_id,
                    };
                    setLocationFieldValues(value, form);
                    lastGeocodedAddressRef.current = normalizedAddress;
                }
            } catch (error) {
                // Handle error if needed
            }
        }, [setLocationFieldValues]);

        const debouncedUpdateLocation = useMemo(
            () => debounce((item, form) => {
                updateLocationFromAddress(item, form);
            }, 1000),
            [updateLocationFromAddress]
        );

        const handleChange = (event, form) => {
            const item = event.target.value;
            if (mapField && !!item) {
                debouncedUpdateLocation(item, form);
            }
            form.setFieldValue(name, item);
            form.setFieldValue(mapFieldName, {
                ...(form.values?.[mapFieldName] || {}),
                address: item,
            });
        };

        useEffect(() => {
            return () => {
                debouncedUpdateLocation.cancel();
            };
        }, [debouncedUpdateLocation]);

        const drawerLocationFields = (latitudeName && longitudeName && geofenceRadiusName) ? (
            <div className="grid grid-cols-3 gap-3 mb-4">
                <Field name={latitudeName}>
                    {({ field, meta }) => (
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-[500] text-[#4a5568]">Latitude</label>
                            <input
                                readOnly
                                type="number"
                                name={field.name}
                                value={field.value || 0}
                                className={`bg-[#F2F2F2] w-full border-[1px] border-neutral-40 border-solid ${(meta.touched && meta.error) && 'border-secondary-red'} h-[44px] rounded-[6px] p-[12px] focus:outline-4 focus:outline-primary-40 focus:border-[1px] focus:border-primary-main`}
                            />
                            {(meta.touched && meta.error) && <span className="text-red-600 text-xs">{meta.error}</span>}
                        </div>
                    )}
                </Field>
                <Field name={longitudeName}>
                    {({ field, meta }) => (
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-[500] text-[#4a5568]">Longitude</label>
                            <input
                                readOnly
                                type="number"
                                name={field.name}
                                value={field.value || 0}
                                className={`bg-[#F2F2F2] w-full border-[1px] border-neutral-40 border-solid ${(meta.touched && meta.error) && 'border-secondary-red'} h-[44px] rounded-[6px] p-[12px] focus:outline-4 focus:outline-primary-40 focus:border-[1px] focus:border-primary-main`}
                            />
                            {(meta.touched && meta.error) && <span className="text-red-600 text-xs">{meta.error}</span>}
                        </div>
                    )}
                </Field>
                <Field name={geofenceRadiusName}>
                    {({ field, form, meta }) => (
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-[500] text-[#4a5568]">Geofence Radius (Meters)</label>
                            <input
                                type="number"
                                name={field.name}
                                value={field.value || 0}
                                min={1}
                                onChange={(event) => form.setFieldValue(field.name, event.target.value)}
                                onBlur={field.onBlur}
                                className={`bg-neutral-light w-full border-[1px] border-neutral-40 border-solid ${(meta.touched && meta.error) && 'border-secondary-red'} h-[44px] rounded-[6px] p-[12px] focus:outline-4 focus:outline-primary-40 focus:border-[1px] focus:border-primary-main`}
                            />
                            {(meta.touched && meta.error) && <span className="text-red-600 text-xs">{meta.error}</span>}
                        </div>
                    )}
                </Field>
            </div>
        ) : undefined;

        return (
            <div className="flex items-center gap-4 w-full">
                <div className="flex flex-col flex-1 gap-1">
                    <div className="flex items-center justify-between">
                        {!!label && (
                            <label
                                className={`font-manropeSB text-[#4a5568] text-[1.8vh] ${labelClassNames || ""}`}
                            >
                                {label} {!!required && <span className="text-red-700">*</span>}
                            </label>)}
                    </div>
                    <Field name={name}>
                        {({ field, form, meta }) => (
                            <div
                                className={`relative inline-block w-full my-2 m-auto`}
                            >
                                <div className="flex justify-between items-center gap-2 w-full">
                                    <input
                                        disabled={disable}
                                        className={classNames(
                                            `${disable ? "bg-[#F2F2F2]" : "bg-neutral-light"} w-full
                         border-[1px] border-neutral-40 border-solid ${(meta.touched && meta.error) && 'border-secondary-red'
                                            } h-[44px] rounded-[6px] p-[12px] focus:outline-4 focus:outline-primary-40 focus:border-[1px] focus:border-primary-main`,
                                        )}
                                        placeholder={placeHolder || ""}
                                        type={type || "text"}
                                        required={required}
                                        value={field?.value?.address || field.value}
                                        onChange={(item) => {
                                            let val = item.target.value;
                                            if (type === "number") {
                                                val = val.replace(/[^0-9]/g, ""); // Allow only positive numbers
                                            }
                                            handleChange({ target: { value: isMobile ? val.slice(0, 10) : val } }, form);
                                        }}
                                        onBlur={(event) => {
                                            debouncedUpdateLocation.cancel();
                                            if (mapField && event.target.value) {
                                                updateLocationFromAddress(event.target.value, form);
                                            }
                                            field.onBlur(event);
                                        }}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        onKeyDown={(e) => {
                                            if (
                                                type === "number" &&
                                                (e.key === "ArrowUp" || e.key === "ArrowDown")
                                            ) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </Field>
                    <span>
                        <ErrorMessage
                            name={name}
                            component="div"
                            className="text-red-600 text-xs"
                        />
                    </span>
                </div>
                <div className="text-[2.2vh] flex items-center gap-4">
                    <h1>Or</h1>
                    <Field name={mapFieldName}>
                        {({ field, form, meta }) => (<GoogleMaps
                            onLocationClick={(e) => {
                                setLocationFieldValues(e, form);
                                form.setFieldValue(
                                    name,
                                    e?.address || ""
                                );
                            }}
                            selectedLocation={field.value}
                            initialLocation={field.value}
                            isError={meta.touched && meta.error}
                            drawerContent={drawerLocationFields}
                        />)}
                    </Field>
                </div>
            </div>
        );
    };

export default MapField;
